import { useState } from "react";
import axios from "axios";
import { Button, Spinner, Alert, Form, Image} from "react-bootstrap";
import icAdd from './assets/img/ic-add-circle.svg';

const CompanyImport = ({ onImportSuccess }) => {
    const [file, setFile] = useState(null);
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const handleFileChange = (event) => {
        const selected = event.target.files?.[0] || null;
        setFile(selected);
        setReport(null);
        setError("");

        if (!selected) return;

        const name = (selected.name || "").toLowerCase();
        if (!name.endsWith(".csv")) {
            setError("Please select a CSV file (.csv).");
        }
    };

    const handleUploadToServer = async () => {
        if (!file) {
            setError("Please select a CSV file.");
            return;
        }

        const name = (file.name || "").toLowerCase();
        if (!name.endsWith(".csv")) {
            setError("Please select a CSV file (.csv).");
            return;
        }

        const formData = new FormData();
        formData.append("file", file);

        setLoading(true);
        setError("");
        setReport(null);

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/api/companies/import`,
                formData,
                {
                    headers: {
                        "x-auth-token": localStorage.getItem("token"),
                        "Content-Type": "multipart/form-data",
                    },
                }
            );
            setReport(response.data);

            if (typeof onImportSuccess === "function") {
                onImportSuccess(); // ✅ refresh list in parent + close modal (if parent does that)
            }

        } catch (error) {
            console.error("Error importing companies:", error);
            const msg =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                "Failed to import companies. Please try again.";
            setError(msg);
        } finally {
            setLoading(false);
        }
    };
    
    return (
        <>  
            <Form onSubmit={handleUploadToServer}>
                <Form.Group controlId="file">
                    <Form.Label>Upload file</Form.Label>
                    <div 
                        className="custom-upload-box mb-1"
                        onClick={() => document.getElementById("hiddenFileInput").click()}
                    >
                        <Image className="upload-icon" src={icAdd} alt="Upload File" />
                        <span className="upload-text">Choose file</span>
                    </div>
                    <Form.Control
                        id="hiddenFileInput"
                        type="file"
                        name="attachment"
                        style={{ display: "none" }}
                        onChange={handleFileChange}
                    />
                    <p className="info">
                        File format: CSV. Max file size: 5mb
                    </p>

                    {file && (
                        <div className="info mb-4 text-muted">
                            Selected file: <strong>{file.name}</strong>
                        </div>
                    )}
                </Form.Group>
                <Button
                    type="submit"
                    variant="primary"
                    onClick={handleUploadToServer}
                    disabled={loading || !file}
                >
                    {loading ? <Spinner animation="border" size="sm" /> : "Upload CSV to Server"} 
                </Button>

                {error && <Alert className="mt-4" variant="danger">{error}</Alert>}

                {report && (
                    <Alert variant="info" className="mt-3">
                        <div><strong>{report.message || "Import finished"}</strong></div>
                        {"totalRows" in report && <div>Total rows: {report.totalRows}</div>}
                        {"processed" in report && <div>Processed: {report.processed}</div>}
                        {"upserted" in report && <div>Inserted : {report.upserted}</div>}
                        {/* {"updated" in report && <div>Updated: {report.updated}</div>} */}
                        {"skippedCount" in report && <div>Skipped: {report.skippedCount}</div>}
                        {/* {"unchangedCount" in report && <div>Unchanged: {report.unchangedCount}</div>} */}
                        {"reviewCount" in report && <div>Marked Under review: {report.reviewCount}</div>}

                        {Array.isArray(report.review) && report.review.length > 0 && (
                            <div className="mt-2">
                                <div><strong>Marked as Under review ({report.reviewCount}):</strong></div>
                                <ul className="mb-0">
                                    {report.review.slice(0, 200).map((r, idx) => (
                                        <li key={idx}>
                                            Row {r.row} — (VAT: {r.vat}) {r.title || "Unknown"} — changed: {Array.isArray(r.changedFields) ? r.changedFields.join(", ") : ""}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {Array.isArray(report.skipped) && report.skipped.length > 0 && (
                            <div className="mt-2">
                                <div><strong>Skipped rows {report.skippedCount}:</strong></div>
                                <ul className="mb-0">
                                    {report.skipped.slice(0, 1000).map((s, idx) => (
                                        <li key={idx}>
                                            Row {s.row} - (VAT: {s.vat || "N/A"}) {s.title || "Unknown"}: {s.reason}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </Alert>
                )}
            </Form>
        </>
    );
};

export default CompanyImport;
